// Payment wire Authorization Response Model
import { IReceiptData, IReceiptObjectData, ReceiptData, ReceiptObjectData, IEmvData, EmvData } from '../common/Receipt';
import type { DeclineGuidance } from "../../neutral/outcomes/DeclineGuidance";

// UserFields is an array of objects with flexible key-value pairs (same as request)
export type UserField = Record<string, string>;

export interface BinInfo {
  readonly country?: string;
  readonly product?: string;
  readonly bin?: string;
  readonly cardusestring?: string;
  readonly gsa?: boolean;
  readonly corporate?: boolean;
  readonly fsa?: boolean;
  readonly subtype?: string;
  readonly purchase?: boolean;
  readonly prepaid?: boolean;
  readonly issuer?: string;
  readonly binlo?: string;
  readonly binhi?: string;
}

export interface IAuthResponse {
  // Core response fields
  readonly respstat?: string;
  readonly retref?: string;
  readonly account?: string;
  readonly expiry?: string;
  readonly token?: string;
  readonly amount?: string;
  /**
   * Tip auth buffer (docs/CARD_TIPPING_ARCHITECTURE.md §5b): the card HOLD,
   * present only when a two-step hold was authorized above its base. `amount`
   * stays the base. Server-written on `/payments/auth`; never a provider field.
   */
  readonly authAmount?: string;
  /** The base the buffered hold covers (equals `amount`). Present with `authAmount` only. */
  readonly authBaseAmount?: string;
  /** True on a tip-auth-buffered hold. Present with `authAmount` only. */
  readonly tipAuthBuffered?: boolean;
  /**
   * A partial approval the location KEPT as a real payment of `amount` (the
   * approved figure); the rest of the bill stays due. Absent on every other
   * answer — an unkept partial is a decline carrying `partial_approval` guidance.
   */
  readonly partialApproval?: boolean;
  /** With `partialApproval`: the amount originally requested, decimal dollars. */
  readonly requestedAmount?: string;
  /**
   * The PaymentRecord status the server wrote for this answer — server-written
   * on `/payments/sale` and `/payments/auth`, never a provider field. The till
   * shows "retrying automatically" only for `offlineDeclinedSoft` /
   * `offlineRecovering` (docs/OFFLINE_PAYMENT_RECOVERY.md); absent on answers
   * from before this field existed.
   */
  readonly paymentRecordStatus?: string;
  /**
   * The card network's transaction id (TRX `HostData.TransactionId`), copied
   * from the neutral `SaleResult.networkTransactionId`. TRX-routed sales only;
   * read by the /sale seam to anchor an offline-recovery campaign.
   */
  readonly networkTransactionId?: string;
  readonly orderId?: string;
  readonly merchid?: string;
  readonly batchid?: string;

  // Response codes and text
  readonly respcode?: string;
  readonly resptext?: string;
  readonly respproc?: string;
  readonly cardproc?: string;

  // Profile information
  readonly profileid?: string;
  readonly acctid?: string;

  // Card on file
  readonly cof?: string;

  // Verification responses
  readonly avsresp?: string;
  readonly cvvresp?: string;

  // BIN information
  readonly binInfo?: BinInfo;
  readonly bintype?: string;

  // Entry mode
  readonly entrymode?: string;

  // Fee information
  readonly fee_amount?: string;
  readonly fee_authcode?: string;
  readonly fee_format?: string;
  readonly fee_merchid?: string;
  readonly fee_retref?: string;
  readonly fee_type?: string;
  readonly fee_value?: string;

  // Decline information
  readonly declineCategory?: string;
  readonly declineCategoryText?: string;
  readonly merchAdviceCode?: string;
  readonly merchAdviceText?: string;
  readonly assocRespCode?: string;
  readonly assocRespText?: string;

  /**
   * Resolved, renderable decline guidance — populated by
   * `attachDeclineGuidance` on every NON-approved response, for BOTH providers.
   *
   * `respcode` and `resptext` have always ridden this wire; what was missing
   * was anything a human could act on, so the POS rendered the gateway's own
   * string ("DECLINE", "Do not honor") as its entire failure message. This
   * field is that missing hop. Resolved at the `operator` audience, because
   * this response shape is the POS contract — cardholder-facing surfaces
   * resolve their own copy from the same catalog rather than reading this.
   *
   * Absent on approvals. Never contains card data.
   */
  readonly decline?: DeclineGuidance;

  // Authorization and signature
  readonly authcode?: string | number;
  readonly signature?: string;

  // Card type information
  readonly commcard?: string;

  // EMV data
  readonly emv?: string;
  readonly emvTagData?: IEmvData;
  /**
   * Issuer-side EMV chip-completion data (TRX-routed responses). The POS forwards
   * this to the IDTech reader's `emv_authenticateTransaction(...)` so
   * the chip can verify the issuer cryptogram and execute scripts.
   * See `Neutral.IssuerEmvResponse` for full semantics.
   */
  readonly issuerEmv?: {
    readonly arc: string;
    readonly authCode?: string;
    readonly issuerAuthenticationData?: string;
    readonly issuerScriptTemplate1?: string;
    readonly issuerScriptTemplate2?: string;
  };

  // Receipt data
  readonly receipt?: IReceiptData;
  readonly receiptObj?: IReceiptObjectData;

  // User defined fields
  readonly userfields?: UserField[];

  // Card display extensions — populated by TRX adapters from the
  // request's instrument (the gateway response itself doesn't echo
  // these). Absent when no adapter supplied them; the `account` / `expiry`
  // / `binInfo` fields above are the fallback.
  /** Cardholder name as printed / encoded on the card. */
  readonly cardholderName?: string;
  /** 2-digit expiry year (e.g. "28" for 2028). Display + audit only. */
  readonly cardExpiryYear?: string;
  /** Masked PAN (e.g. `"4761********0023"`) — richer display than account. */
  readonly maskedPan?: string;
  /** Reader hardware serial that produced this read — audit-relevant. */
  readonly deviceSerialNum?: string;
  /**
   * Keyed hash of the network Payment Account Reference (`ci1_…`) — the card
   * identity key card-linked loyalty matches on (docs/CARD_LINKED_LOYALTY.md).
   * Never the raw PAR. TRX-only today; absent when the response carried no PAR
   * or the platform secret is unset.
   */
  readonly cardIdentityKey?: string;
  /**
   * The TRX merchant account (MID) this charge was processed under — what a
   * token minted by this charge (inline StorageSafe Generate) must be pinned
   * to. Set by the payment orchestrator; never secret.
   */
  readonly merchantAccountId?: string;
}

export class AuthResponse implements IAuthResponse {
  // Core response fields
  readonly respstat?: string;
  readonly retref?: string;
  readonly account?: string;
  readonly expiry?: string;
  readonly token?: string;
  readonly amount?: string;
  readonly orderId?: string;
  readonly merchid?: string;
  readonly batchid?: string;

  // Response codes and text
  readonly respcode?: string;
  readonly resptext?: string;
  readonly respproc?: string;
  readonly cardproc?: string;

  // Profile information
  readonly profileid?: string;
  readonly acctid?: string;

  // Card on file
  readonly cof?: string;

  // Verification responses
  readonly avsresp?: string;
  readonly cvvresp?: string;

  // BIN information
  readonly binInfo?: BinInfo;
  readonly bintype?: string;

  // Entry mode
  readonly entrymode?: string;

  // Fee information
  readonly fee_amount?: string;
  readonly fee_authcode?: string;
  readonly fee_format?: string;
  readonly fee_merchid?: string;
  readonly fee_retref?: string;
  readonly fee_type?: string;
  readonly fee_value?: string;

  // Decline information
  readonly declineCategory?: string;
  readonly declineCategoryText?: string;
  readonly merchAdviceCode?: string;
  readonly merchAdviceText?: string;
  readonly assocRespCode?: string;
  readonly assocRespText?: string;

  /**
   * Resolved, renderable decline guidance — populated by
   * `attachDeclineGuidance` on every NON-approved response, for BOTH providers.
   *
   * `respcode` and `resptext` have always ridden this wire; what was missing
   * was anything a human could act on, so the POS rendered the gateway's own
   * string ("DECLINE", "Do not honor") as its entire failure message. This
   * field is that missing hop. Resolved at the `operator` audience, because
   * this response shape is the POS contract — cardholder-facing surfaces
   * resolve their own copy from the same catalog rather than reading this.
   *
   * Absent on approvals. Never contains card data.
   */
  readonly decline?: DeclineGuidance;

  // Authorization and signature
  readonly authcode?: string | number;
  readonly signature?: string;

  // Card type information
  readonly commcard?: string;

  // EMV data
  readonly emv?: string;
  readonly emvTagData?: EmvData;

  // Receipt data
  readonly receipt?: ReceiptData;
  readonly receiptObj?: ReceiptObjectData;

  // User defined fields
  readonly userfields?: UserField[];

  // Card display extensions — see [IAuthResponse] for full doc.
  readonly cardholderName?: string;
  readonly cardExpiryYear?: string;
  readonly maskedPan?: string;
  readonly deviceSerialNum?: string;
  readonly cardIdentityKey?: string;
  readonly merchantAccountId?: string;

  constructor(data: IAuthResponse) {
    // Core response fields
    this.respstat = data.respstat;
    this.retref = data.retref;
    this.account = data.account;
    this.expiry = data.expiry;
    this.token = data.token;
    this.amount = data.amount;
    this.orderId = data.orderId;
    this.merchid = data.merchid;
    this.batchid = data.batchid;

    // Response codes and text
    this.respcode = data.respcode;
    this.resptext = data.resptext;
    this.respproc = data.respproc;
    this.cardproc = data.cardproc;

    // Profile information
    this.profileid = data.profileid;
    this.acctid = data.acctid;

    // Card on file
    this.cof = data.cof;

    // Verification responses
    this.avsresp = data.avsresp;
    this.cvvresp = data.cvvresp;

    // BIN information
    this.binInfo = data.binInfo;
    this.bintype = data.bintype;

    // Entry mode
    this.entrymode = data.entrymode;

    // Fee information
    this.fee_amount = data.fee_amount;
    this.fee_authcode = data.fee_authcode;
    this.fee_format = data.fee_format;
    this.fee_merchid = data.fee_merchid;
    this.fee_retref = data.fee_retref;
    this.fee_type = data.fee_type;
    this.fee_value = data.fee_value;

    // Decline information
    this.declineCategory = data.declineCategory;
    this.declineCategoryText = data.declineCategoryText;
    this.merchAdviceCode = data.merchAdviceCode;
    this.merchAdviceText = data.merchAdviceText;
    this.assocRespCode = data.assocRespCode;
    this.assocRespText = data.assocRespText;
    this.decline = data.decline;

    // Authorization and signature
    this.authcode = data.authcode;
    this.signature = data.signature;

    // Card type information
    this.commcard = data.commcard;

    // EMV data
    this.emv = data.emv;
    if (data.emvTagData) {
      this.emvTagData = new EmvData(data.emvTagData);
    }

    // Receipt data
    if (data.receipt) {
      this.receipt = new ReceiptData(data.receipt);
    }
    if (data.receiptObj) {
      this.receiptObj = new ReceiptObjectData(data.receiptObj);
    }

    // User defined fields
    this.userfields = data.userfields;

    // Card display extensions
    this.cardholderName = data.cardholderName;
    this.cardExpiryYear = data.cardExpiryYear;
    this.maskedPan = data.maskedPan;
    this.deviceSerialNum = data.deviceSerialNum;
    this.cardIdentityKey = data.cardIdentityKey;
    this.merchantAccountId = data.merchantAccountId;
  }

  // Helper methods for response status
  isApproved(): boolean {
    return this.respstat === 'A';
  }

  isDeclined(): boolean {
    return this.respstat === 'C';
  }

  shouldRetry(): boolean {
    return this.respstat === 'B';
  }

  // Helper methods for transaction types
  isSuccessful(): boolean {
    return this.isApproved();
  }

  hasToken(): boolean {
    return this.token !== undefined && this.token.length > 0;
  }

  hasProfile(): boolean {
    return this.profileid !== undefined && this.profileid.length > 0;
  }

  // Helper methods for verification results
  isAVSMatch(): boolean {
    return this.avsresp === 'Y';
  }

  isCVVMatch(): boolean {
    return this.cvvresp === 'M';
  }

  // Helper methods for card information
  isCorporateCard(): boolean {
    return this.commcard === 'Y' || this.binInfo?.corporate === true;
  }

  isGSACard(): boolean {
    return this.binInfo?.gsa === true;
  }

  isPrepaidCard(): boolean {
    return this.binInfo?.prepaid === true;
  }

  // Helper methods for fee information
  hasFee(): boolean {
    return this.fee_amount !== undefined && parseFloat(this.fee_amount) > 0;
  }

  isSurchargeApplied(): boolean {
    return this.fee_type === 'SURCHRG';
  }

  isSurchargeWaived(): boolean {
    return this.fee_type === 'SURCHRG_WAIVED';
  }

  // Helper methods for decline handling
  shouldNeverRetryDecline(): boolean {
    return this.declineCategory === '1';
  }

  canRetryLater(): boolean {
    return this.declineCategory === '2';
  }

  shouldFixDataAndRetry(): boolean {
    return this.declineCategory === '3';
  }

  // Helper methods for COF transactions
  isCustomerInitiated(): boolean {
    return this.cof === 'C';
  }

  isMerchantInitiated(): boolean {
    return this.cof === 'M';
  }

  // Helper methods for EMV data
  hasEMVData(): boolean {
    return this.emv !== undefined || this.emvTagData !== undefined;
  }

  // Utility methods
  getAmount(): number {
    return this.amount ? parseFloat(this.amount) : 0;
  }

  getFeeAmount(): number {
    return this.fee_amount ? parseFloat(this.fee_amount) : 0;
  }

  getCardType(): string {
    return this.binInfo?.product || 'Unknown';
  }

  getCardBrand(): string {
    const product = this.binInfo?.product;
    switch (product) {
      case 'A': return 'American Express';
      case 'D': return 'Discover';
      case 'M': return 'Mastercard';
      case 'V': return 'Visa';
      case 'N': return 'Non-co-branded Debit';
      default: return 'Unknown';
    }
  }

  /**
   * Creates a plain JavaScript object suitable for API responses.
   */
  toJSON(): IAuthResponse {
    const result: any = {};

    // Only include defined fields to keep the JSON clean
    if (this.respstat !== undefined) result.respstat = this.respstat;
    if (this.retref !== undefined) result.retref = this.retref;
    if (this.account !== undefined) result.account = this.account;
    if (this.expiry !== undefined) result.expiry = this.expiry;
    if (this.token !== undefined) result.token = this.token;
    if (this.amount !== undefined) result.amount = this.amount;
    if (this.orderId !== undefined) result.orderId = this.orderId;
    if (this.merchid !== undefined) result.merchid = this.merchid;
    if (this.batchid !== undefined) result.batchid = this.batchid;
    if (this.respcode !== undefined) result.respcode = this.respcode;
    if (this.resptext !== undefined) result.resptext = this.resptext;
    if (this.respproc !== undefined) result.respproc = this.respproc;
    if (this.cardproc !== undefined) result.cardproc = this.cardproc;
    if (this.profileid !== undefined) result.profileid = this.profileid;
    if (this.acctid !== undefined) result.acctid = this.acctid;
    if (this.cof !== undefined) result.cof = this.cof;
    if (this.avsresp !== undefined) result.avsresp = this.avsresp;
    if (this.cvvresp !== undefined) result.cvvresp = this.cvvresp;
    if (this.binInfo !== undefined) result.binInfo = this.binInfo;
    if (this.bintype !== undefined) result.bintype = this.bintype;
    if (this.entrymode !== undefined) result.entrymode = this.entrymode;
    if (this.fee_amount !== undefined) result.fee_amount = this.fee_amount;
    if (this.fee_authcode !== undefined) result.fee_authcode = this.fee_authcode;
    if (this.fee_format !== undefined) result.fee_format = this.fee_format;
    if (this.fee_merchid !== undefined) result.fee_merchid = this.fee_merchid;
    if (this.fee_retref !== undefined) result.fee_retref = this.fee_retref;
    if (this.fee_type !== undefined) result.fee_type = this.fee_type;
    if (this.fee_value !== undefined) result.fee_value = this.fee_value;
    if (this.declineCategory !== undefined) result.declineCategory = this.declineCategory;
    if (this.declineCategoryText !== undefined) result.declineCategoryText = this.declineCategoryText;
    if (this.merchAdviceCode !== undefined) result.merchAdviceCode = this.merchAdviceCode;
    if (this.merchAdviceText !== undefined) result.merchAdviceText = this.merchAdviceText;
    if (this.assocRespCode !== undefined) result.assocRespCode = this.assocRespCode;
    if (this.assocRespText !== undefined) result.assocRespText = this.assocRespText;
    if (this.decline !== undefined) result.decline = this.decline;
    if (this.authcode !== undefined) result.authcode = this.authcode;
    if (this.signature !== undefined) result.signature = this.signature;
    if (this.commcard !== undefined) result.commcard = this.commcard;
    if (this.emv !== undefined) result.emv = this.emv;
    if (this.emvTagData !== undefined) result.emvTagData = this.emvTagData;
    if (this.receipt !== undefined) result.receipt = this.receipt;
    if (this.receiptObj !== undefined) result.receiptObj = this.receiptObj;
    if (this.userfields !== undefined) result.userfields = this.userfields;

    return result as IAuthResponse;
  }

  static fromJSON(data: Record<string, any>): AuthResponse {
    const responseData = { ...data };
    if (typeof responseData.receipt === 'string') {
      responseData.receipt = JSON.parse(responseData.receipt);
    }
    if (typeof responseData.receiptObj === 'string') {
      responseData.receiptObj = JSON.parse(responseData.receiptObj);
    }
    if (typeof responseData.emvTagData === 'string') {
      responseData.emvTagData = JSON.parse(responseData.emvTagData);
    }
    return new AuthResponse(responseData as IAuthResponse);
  }
} 